import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import _ from 'lodash';
import { CalendarX } from 'lucide-react';
import { StyledGameCard, StyledGameDateHeader } from 'modules/games/ui/components/games-list/games-list';
import { fromMatchOverlayState } from 'modules/devices/domain/constants';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTrackEventOnce } from 'shared/analytics/use-track-event-once';
import { deriveAsyncState } from 'shared/utils/async-state';
import { getDateLocale } from 'shared/i18n';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDButton from 'ud-ui/components/button';
import UDEmptyState from 'ud-ui/components/empty-state';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDText from 'ud-ui/components/text';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { openSupportChat } from 'modules/support-chat/domain/utils';
import { CalendarGame } from 'modules/schedule/domain/interfaces/calendar';
import { toDayKey, toMonthKey } from 'modules/schedule/domain/utils/calendar-months';
import { getMatchResultPath } from 'modules/schedule/domain/utils/match-result-path';
import { buildChampionshipOptions, filterMatchesByChampionships } from 'modules/schedule/domain/utils/championship-filter';
import CalendarStrip from 'modules/schedule/ui/components/calendar-strip';
import ChampionshipFilter from 'modules/schedule/ui/components/championship-filter';
import { useChampionshipFilter } from 'modules/schedule/ui/hooks/use-championship-filter';
import { useMonthMatches } from 'modules/schedule/ui/hooks/use-month-matches';
import { useSchedule } from 'modules/schedule/ui/hooks/use-schedule';
import { ChampionshipHeader, DayMatches, GamesList, GamesWrapper, MainBlock } from './styles';

function Calendar() {
  const { i18n, t } = useLingui();
  const navigate = useNavigate();
  const { info, isLoading: isLoadingOrganizer } = useOrganizer();
  const { currentDate, selectedDate } = useSchedule();
  const { matchesByDay, visibleMatches, loadedMonths, loadingMonths, failedMonths, retry } = useMonthMatches(
    info?.id,
    currentDate,
    selectedDate,
  );
  const { championshipFilter, changeChampionshipFilter, resetChampionshipFilter } = useChampionshipFilter();
  const { setHeaderContent } = useMainLayout();

  useLayoutEffect(() => {
    StepperMainTool.setByPath('stepper:main:manageTournament', true);
  }, []);

  // Раз за монтирование страницы (`use-track-event-once.ts`).
  useTrackEventOnce('Championship Management Opened', {});

  // Один тост на сбой: упавший месяц сам не повторяется, поэтому «упало» → «не упало» → «упало» — это уже новая попытка.
  const hasFailedMonths = failedMonths.length > 0;
  useEffect(() => {
    if (hasFailedMonths) toast.error(t`Не удалось загрузить календарь`);
  }, [hasFailedMonths, t]);

  // Месяц выбранного дня: от него, а не от «грузится ли что-то», зависит, что показать под календарём.
  const selectedMonthKey = useMemo(() => {
    const date = new Date(selectedDate);
    return toMonthKey({ month: date.getMonth(), year: date.getFullYear() });
  }, [selectedDate]);
  const selectedDayKey = useMemo(() => toDayKey(new Date(selectedDate)), [selectedDate]);

  // Варианты — турниры показанных месяцев; фильтр действует и на отметки календаря, и на блок дня.
  const championshipOptions = useMemo(
    () => buildChampionshipOptions(visibleMatches, championshipFilter),
    [visibleMatches, championshipFilter],
  );
  const filteredMatchesByDay = useMemo(
    () => filterMatchesByChampionships(matchesByDay, championshipFilter),
    [matchesByDay, championshipFilter],
  );
  const selectedDayMatches = filteredMatchesByDay[selectedDayKey];
  // Матчи у дня есть, но фильтр скрыл их все: сбросить фильтр — выход; у дня без матчей вовсе сброс ничего не даст.
  const isHiddenByFilter = championshipFilter.length > 0 && !selectedDayMatches && (matchesByDay[selectedDayKey]?.length ?? 0) > 0;

  const dateKey = useMemo(() => {
    return format(new Date(selectedDate), 'dd MMMM, EEEEEE', { locale: getDateLocale(i18n.locale) });
  }, [selectedDate, i18n.locale]);

  // Ссылка на табло больше не строится по матчу — она принадлежит устройству,
  // поэтому кнопка ведёт на «Устройства», где эту ссылку и копируют.
  const onShowGameTemplatesClick = useCallback(() => {
    navigate(Pages.DEVICES.INDEX, { state: fromMatchOverlayState });
  }, [navigate]);

  // Обработчик отдаём карточке, только если у матча есть страница: иначе кнопки нет вовсе.
  const getShowResultClick = useCallback(
    (game: CalendarGame) => {
      const path = getMatchResultPath(game);
      return path ? () => navigate(path) : undefined;
    },
    [navigate],
  );

  // Группы дня по турниру (`id`, не название: у двух турниров бывает одно имя).
  const championshipGroups = useMemo(() => _.values(_.groupBy(selectedDayMatches ?? [], 'championship.id')), [selectedDayMatches]);

  // Четыре ветки по месяцу выбранного дня: ещё не загружен → загрузка; упал → ошибка с «Повторить»; загружен без матчей
  // → «нет матчей»; есть матчи → список. «Нет матчей» без загруженного месяца не показывается никогда.
  const dayState = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: championshipGroups.length,
        hasCurrentData: loadedMonths.includes(selectedMonthKey),
        isFetching: isLoadingOrganizer || loadingMonths.includes(selectedMonthKey),
        isError: failedMonths.includes(selectedMonthKey),
      }),
    [championshipGroups.length, failedMonths, isLoadingOrganizer, loadedMonths, loadingMonths, selectedMonthKey],
  );

  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>Расписание</Trans>
      </UDHeaderTitle>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  return (
    <MainBlock>
      <CalendarStrip
        matchesByDay={filteredMatchesByDay}
        failedMonths={failedMonths}
        isLoading={loadingMonths.length > 0}
        onRetry={retry}
        filter={<ChampionshipFilter options={championshipOptions} value={championshipFilter} onChange={changeChampionshipFilter} />}
      />
      <UDAsyncContent
        state={dayState}
        minHeight={120}
        data-testid="selected-day-matches"
        error={
          <UDEmptyState
            className="py-6"
            title={<Trans>Не удалось загрузить матчи месяца</Trans>}
            action={
              <UDButton variant="secondary" onClick={() => retry(selectedMonthKey)}>
                <Trans>Повторить</Trans>
              </UDButton>
            }
          />
        }
        empty={
          <UDEmptyState
            className="py-6"
            icon={<CalendarX size={28} />}
            title={isHiddenByFilter ? <Trans>В этот день нет матчей выбранных турниров</Trans> : <Trans>В этот день матчей нет</Trans>}
            subtitle={
              isHiddenByFilter ? (
                <Trans>Сбросьте фильтр, чтобы увидеть остальные матчи дня</Trans>
              ) : (
                <Trans>Выберите в календаре день с матчами</Trans>
              )
            }
            action={
              isHiddenByFilter ? (
                <UDButton variant="secondary" onClick={resetChampionshipFilter}>
                  <Trans>Сбросить фильтр</Trans>
                </UDButton>
              ) : (
                <UDButton variant="secondary" onClick={openSupportChat}>
                  <Trans>Сообщить об ошибке</Trans>
                </UDButton>
              )
            }
          />
        }
      >
        <DayMatches key={selectedDayKey} className="mb-6 w-full last:mb-0" data-testid="day-matches">
          <StyledGameDateHeader>
            <p>{dateKey}</p>
          </StyledGameDateHeader>
          <GamesWrapper>
            {championshipGroups.map((games) => {
              const { championship } = games[0];
              const rounds = _.uniq(games.map((game) => game.round?.name).filter(Boolean));

              return (
                <GamesList key={championship.id}>
                  <ChampionshipHeader className={'ChampionshipHeader'}>
                    <UDText type={'subhead'} style={{ fontWeight: 'bold' }}>
                      {championship.name}
                    </UDText>
                    {rounds.length > 0 && (
                      <UDText type={'subhead'} className="text-inkSecondary ml-2" data-testid="championship-rounds">
                        · {rounds.join(', ')}
                      </UDText>
                    )}
                  </ChampionshipHeader>
                  {games.map((game) => (
                    <StyledGameCard
                      key={game.id}
                      game={game}
                      championshipId={game.championship.id}
                      onShowTemplatesClick={onShowGameTemplatesClick}
                      onShowResultClick={getShowResultClick(game)}
                    />
                  ))}
                </GamesList>
              );
            })}
          </GamesWrapper>
        </DayMatches>
      </UDAsyncContent>
    </MainBlock>
  );
}

export default Calendar;
